// src/utils/roleIconMap.ts
//
// Maps iconKey strings (stored in md_person_role.icon_key) to react-icons components.
// Admin picks an iconKey from the icon picker in the MD panel; the frontend renders
// the corresponding component here.
//
// To add a new icon: import it and add an entry to ROLE_ICON_MAP.

import React from "react";
import {
  // People / roles
  FaUser, FaUserTie, FaUsers, FaUserMd, FaUserGraduate,
  FaRunning, FaHandPaper, FaDumbbell, FaBriefcase,
  FaBuilding, FaFlag, FaTv, FaBinoculars,
  // Sport balls
  FaFutbol, FaBasketballBall, FaVolleyballBall, FaFootballBall,
  FaBaseballBall, FaHockeyPuck, FaTableTennis,
  // Charts / analysis
  FaChartBar, FaChartLine, FaChartPie,
  // Medical
  FaHeartbeat, FaBrain, FaAppleAlt, FaFirstAid, FaHospital,
  // Tools / ops
  FaTools, FaClipboardList, FaWrench,
  // Media / video
  FaVideo, FaCamera, FaMicrophone, FaBullhorn,
  // Finance / mgmt
  FaHandshake, FaMoneyBillWave, FaCrown, FaStar, FaMedal,
  // Misc
  FaShieldAlt, FaTrophy, FaKey, FaLink, FaGlobe, FaBan,
  FaTshirt, FaSocks, FaStopwatch,
} from "react-icons/fa";
import { GiWhistle, GiSoccerBall, GiBoxingGlove, GiTrophy } from "react-icons/gi";
import { MdSportsHandball, MdSportsMartialArts, MdOutlineSportsKabaddi } from "react-icons/md";
import { BiBody } from "react-icons/bi";
import { RiMentalHealthLine } from "react-icons/ri";

// The component type accepted by react-icons
type IconComponent = React.ComponentType<{ size?: number; className?: string }>;

/**
 * Static map from iconKey string → react-icons component.
 * iconKey values are stored in md_person_role.icon_key.
 * The admin picks one via the icon picker in the MD panel.
 */
export const ROLE_ICON_MAP: Record<string, IconComponent> = {
  // People / roles
  FaUser,
  FaUserTie,
  FaUsers,
  FaUserMd,
  FaUserGraduate,
  FaRunning,
  FaHandPaper,
  FaDumbbell,
  FaBriefcase,
  FaBuilding,
  FaFlag,
  FaTv,
  FaBinoculars,

  // Sport balls
  FaFutbol,
  FaBasketballBall,
  FaVolleyballBall,
  FaFootballBall,
  FaBaseballBall,
  FaHockeyPuck,
  FaTableTennis,

  // Charts / analysis
  FaChartBar,
  FaChartLine,
  FaChartPie,

  // Medical
  FaHeartbeat,
  FaBrain,
  FaAppleAlt,
  FaFirstAid,
  FaHospital,

  // Tools / ops
  FaTools,
  FaClipboardList,
  FaWrench,

  // Media / video
  FaVideo,
  FaCamera,
  FaMicrophone,
  FaBullhorn,

  // Finance / mgmt
  FaHandshake,
  FaMoneyBillWave,
  FaCrown,
  FaStar,
  FaMedal,

  // Misc
  FaShieldAlt,
  FaTrophy,
  FaKey,
  FaLink,
  FaGlobe,
  FaBan,
  FaTshirt,
  FaSocks,
  FaStopwatch,

  // gi icons
  GiWhistle,
  GiSoccerBall,
  GiBoxingGlove,
  GiTrophy,

  // md icons
  MdSportsHandball,
  MdSportsMartialArts,
  MdOutlineSportsKabaddi,

  // bi icons
  BiBody,

  // ri icons
  RiMentalHealthLine,
};

/** All available icon keys for the admin icon picker. */
export const ROLE_ICON_KEYS = Object.keys(ROLE_ICON_MAP);

/**
 * Render the icon for a given iconKey string.
 * Falls back to FaUser when the key is not found.
 */
export const RoleIconByKey: React.FC<{ iconKey: string; size?: number; className?: string }> = ({
  iconKey,
  size = 14,
  className,
}) => {
  const Icon = ROLE_ICON_MAP[iconKey] ?? FaUser;
  return React.createElement(Icon, { size, className });
};